import type { CSSProperties } from 'react'
import { css, styled } from 'styled-components'

import type { DynamicSizeVariables } from '../../theme/generated/theme-variables'

import {
  TypographyBreakpoint,
  type TypographyProps,
  type TypographyVariants,
} from './Typography.Types'

export const typographyStyles = (variant: TypographyVariants) => {
  switch (variant) {
    case 'Poppins/H1': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H1'] || '2rem'};
        font-weight: 700;
        line-height: 120%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H1'] || '2rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H1'] || '2.25rem'};
        }
      `
    }
    case 'Poppins/H1 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H1'] || '2rem'};
        font-weight: 500;
        line-height: 120%;
        letter-spacing: -0.03em;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H1'] || '2rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H1'] || '2.25rem'};
        }
      `
    }
    case 'Poppins/H2': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H2'] || '1.75rem'};
        font-weight: 700;
        line-height: 120%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H2'] || '1.75rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H2'] || '2rem'};
        }
      `
    }
    case 'Poppins/H2 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H2'] || '1.75rem'};
        font-weight: 500;
        line-height: 120%;
        letter-spacing: -0.03em;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H2'] || '1.75rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H2'] || '2rem'};
        }
      `
    }
    case 'Poppins/H3': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H3'] || '1.5rem'};
        font-weight: 600;
        line-height: 120%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H3'] || '1.5rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H3'] || '1.75rem'};
        }
      `
    }
    case 'Poppins/H3 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H3'] || '1.5rem'};
        font-weight: 500;
        line-height: 120%;
        letter-spacing: -0.03em;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H3'] || '1.5rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H3'] || '1.75rem'};
        }
      `
    }
    case 'Poppins/H4': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H4'] || '1.25rem'};
        font-weight: 600;
        line-height: 120%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H4'] || '1.25rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H4'] || '1.5rem'};
        }
      `
    }
    case 'Poppins/H4 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/H4'] || '1.25rem'};
        font-weight: 500;
        line-height: 120%;
        letter-spacing: -0.03em;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/H4'] || '1.25rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/H4'] || '1.5rem'};
        }
      `
    }
    case 'Poppins/T1': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T1'] || '1.125rem'};
        font-weight: 400;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T1'] || '1.125rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T1'] || '1.125rem'};
        }
      `
    }
    case 'Poppins/T1 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T1'] || '1.125rem'};
        font-weight: 500;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T1'] || '1.125rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T1'] || '1.125rem'};
        }
      `
    }
    case 'Poppins/T1 - Semibold': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T1'] || '1.125rem'};
        font-weight: 600;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T1'] || '1.125rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T1'] || '1.125rem'};
        }
      `
    }
    case 'Poppins/T2': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T2'] || '1rem'};
        font-weight: 400;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T2'] || '1rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T2'] || '1rem'};
        }
      `
    }
    case 'Poppins/T2 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T2'] || '1rem'};
        font-weight: 500;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T2'] || '1rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T2'] || '1rem'};
        }
      `
    }
    case 'Poppins/T2 - Semibold': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T2'] || '1rem'};
        font-weight: 600;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T2'] || '1rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T2'] || '1rem'};
        }
      `
    }
    case 'Poppins/T3': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T3'] || '0.875rem'};
        font-weight: 400;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T3'] || '0.875rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T3'] || '0.875rem'};
        }
      `
    }
    case 'Poppins/T3 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T3'] || '0.875rem'};
        font-weight: 500;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T3'] || '0.875rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T3'] || '0.875rem'};
        }
      `
    }
    case 'Poppins/T3 - Semibold': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T3'] || '0.875rem'};
        font-weight: 600;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T3'] || '0.875rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T3'] || '0.875rem'};
        }
      `
    }
    case 'Poppins/T4': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T4'] || '0.75rem'};
        font-weight: 400;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T4'] || '0.75rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T4'] || '0.75rem'};
        }
      `
    }
    case 'Poppins/T4 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T4'] || '0.75rem'};
        font-weight: 500;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T4'] || '0.75rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T4'] || '0.75rem'};
        }
      `
    }
    case 'Poppins/T4 - Semibold': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T4'] || '0.75rem'};
        font-weight: 600;
        line-height: 140%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T4'] || '0.75rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T4'] || '0.75rem'};
        }
      `
    }
    case 'Poppins/T5': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T5'] || '0.625rem'};
        font-weight: 400;
        line-height: 110%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T5'] || '0.625rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T5'] || '0.625rem'};
        }
      `
    }
    case 'Poppins/T5 - Medium': {
      return css`
        font-family: 'Poppins', sans-serif;
        font-size: ${({ theme }) => theme?.dynamicSizes?.smallMobile['Typography/T5'] || '0.625rem'};
        font-weight: 500;
        line-height: 110%;
        letter-spacing: 0;

        @media (min-width: ${TypographyBreakpoint.largeMobile}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.mobile['Typography/T5'] || '0.625rem'};
        }

        @media (min-width: ${TypographyBreakpoint.desktop}px) {
          font-size: ${({ theme }) => theme?.dynamicSizes?.desktop['Typography/T5'] || '0.625rem'};
        }
      `
    }
    default: {
      throw new Error('Unknown typography variant')
    }
  }
}

export const typographyObjectWithSizes = (
  variant: TypographyVariants,
  sizes: DynamicSizeVariables
): CSSProperties => {
  switch (variant) {
    case 'Poppins/H1': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H1'] ?? '2rem',
        fontWeight: 700,
        lineHeight: '120%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/H1 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H1'] ?? '2rem',
        fontWeight: 500,
        lineHeight: '120%',
        letterSpacing: '-0.03em',
      }
    }
    case 'Poppins/H2': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H2'] ?? '1.75rem',
        fontWeight: 700,
        lineHeight: '120%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/H2 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H2'] ?? '1.75rem',
        fontWeight: 500,
        lineHeight: '120%',
        letterSpacing: '-0.03em',
      }
    }
    case 'Poppins/H3': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H3'] ?? '1.5rem',
        fontWeight: 600,
        lineHeight: '120%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/H3 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H3'] ?? '1.5rem',
        fontWeight: 500,
        lineHeight: '120%',
        letterSpacing: '-0.03em',
      }
    }
    case 'Poppins/H4': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H4'] ?? '1.25rem',
        fontWeight: 600,
        lineHeight: '120%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/H4 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/H4'] ?? '1.25rem',
        fontWeight: 500,
        lineHeight: '120%',
        letterSpacing: '-0.03em',
      }
    }
    case 'Poppins/T1': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T1'] ?? '1.125rem',
        fontWeight: 400,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T1 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T1'] ?? '1.125rem',
        fontWeight: 500,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T1 - Semibold': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T1'] ?? '1.125rem',
        fontWeight: 600,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T2': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T2'] ?? '1rem',
        fontWeight: 400,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T2 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T2'] ?? '1rem',
        fontWeight: 500,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T2 - Semibold': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T2'] ?? '1rem',
        fontWeight: 600,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T3': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T3'] ?? '0.875rem',
        fontWeight: 400,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T3 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T3'] ?? '0.875rem',
        fontWeight: 500,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T3 - Semibold': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T3'] ?? '0.875rem',
        fontWeight: 600,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T4': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T4'] ?? '0.75rem',
        fontWeight: 400,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T4 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T4'] ?? '0.75rem',
        fontWeight: 500,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T4 - Semibold': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T4'] ?? '0.75rem',
        fontWeight: 600,
        lineHeight: '140%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T5': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T5'] ?? '0.625rem',
        fontWeight: 400,
        lineHeight: '110%',
        letterSpacing: '0',
      }
    }
    case 'Poppins/T5 - Medium': {
      return {
        fontFamily: "'Poppins', sans-serif",
        fontSize: sizes['Typography/T5'] ?? '0.625rem',
        fontWeight: 500,
        lineHeight: '110%',
        letterSpacing: '0',
      }
    }
    default: {
      throw new Error('Unknown typography variant')
    }
  }
}

export const Typography = styled.p<TypographyProps>`
  color: ${({ $color }) => $color || 'inherit'};
  margin: 0;
  padding: 0;
  text-align: ${({ $textAlign }) => $textAlign || 'inherit'};
  ${({ $variant }) => typographyStyles($variant)}
`
